import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, ArrowRight, BookOpen, PenTool, TrendingUp, Award, BarChart3,
  CheckCircle, Target, TreePine, Activity, Quote, Star, Search
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const SKILLS = [
  {
    icon: PenTool,
    title: 'Honest Self-Reflection',
    description: 'The post-round reflection is where learning happens — but only if it\'s honest. Most golfers either beat themselves up or gloss over mistakes. Neither produces growth. Honest reflection means looking at what happened factually, without judgement, and extracting the lesson.',
    onCourse: 'In the 19th hole or on the drive home, ask yourself three questions: (1) What did I do well today? (2) Where did I lose shots to my mental game? (3) What\'s one thing I\'ll commit to next time?',
    offCourse: 'Keep a post-round reflection journal. Answer the three questions within 2 hours of finishing. The closer to the round, the more accurate your memory. Be specific, not general.',
    inApp: 'Your Diary is built for this. Write a post-round reflection after every competitive round. Tag it with the QMG Blueprint areas you applied. Over time, your entries become a rich record of growth.',
  },
  {
    icon: Search,
    title: 'Identifying Patterns',
    description: 'Individual rounds are noisy — luck, weather, course conditions all play a role. But over 10, 20, 50 rounds, patterns emerge. Do you always fade on the back nine? Do your scores spike after a triple bogey? Does your mental score drop on windy days? These patterns are where the real insights live.',
    onCourse: 'After each round, note specific moments: "I lost focus on holes 7-9 after the triple on 6." "I played the last 6 holes one under after resetting on 13." These data points build the picture.',
    offCourse: 'Review your last 10 rounds monthly. Look for recurring themes in your diary entries, scorecard trends, and game stats. What\'s your most common mental error? That\'s your priority work.',
    inApp: 'This is where QMG\'s Reports section shines. Performance Scorecard trends, score vs mental score correlations, session type breakdowns — all designed to surface patterns you can\'t see in the moment.',
  },
  {
    icon: TrendingUp,
    title: 'Saving Shots Through Momentum',
    description: 'The idea of "saving one shot" is a game-changer. Not through a brilliant recovery, but through managing your response to setbacks so effectively that you prevent the second and third bad shots that usually follow the first.',
    onCourse: 'After a bad hole, ask: "Can I save one shot over the next three holes by staying committed?" A bogey followed by three pars is infinitely better than a bogey followed by two more bogeys because you were still angry.',
    offCourse: 'Review your scorecards and identify "snowball" stretches — sequences of bad holes triggered by one bad moment. How many shots could you have saved with better recovery? That number is your opportunity.',
    inApp: 'Game Stats tracking shows score patterns across holes. Your In-Between and After scorecard scores reveal whether you\'re managing momentum or letting it manage you.',
  },
  {
    icon: BarChart3,
    title: 'Process Review Over Score Review',
    description: 'Reviewing your score tells you what happened. Reviewing your process tells you WHY. After each round, the score is just one data point. The more valuable data is: how well did you execute your pre-shot routine? How quickly did you recover from setbacks? How committed were your swings?',
    onCourse: 'Give yourself two scores after each round: your actual score AND a process score out of 10. Over time, you\'ll see that process scores predict improvement better than actual scores.',
    offCourse: 'Create a simple post-round checklist: Routine completion (%), Action level (1-10), Recovery speed (1-10), Acceptance of outcomes (1-10). Track these alongside your actual score.',
    inApp: 'Performance Scorecards ARE your process review. The 16 criteria across Before, During, In-Between, and After give you a complete picture of your mental process — far more useful than just a number.',
  },
  {
    icon: Star,
    title: 'Celebrating Growth, Not Just Results',
    description: 'If you only celebrate low scores, you miss the progress happening underneath. Shooting 82 with a process score of 9/10 is better than shooting 78 with a process score of 4/10 — because the 82 is sustainable and the 78 is a house of cards.',
    onCourse: 'After each round, find one moment of genuine mental growth — regardless of the score. Maybe you recovered brilliantly from a bad start. Maybe you stayed committed on the 18th when you usually check out. Acknowledge it.',
    offCourse: 'Keep a "growth wins" list. Every week, write down one mental game victory. Over a season, this list becomes powerful evidence of how far you\'ve come — evidence your score alone can\'t show.',
    inApp: 'Your Diary is the perfect place for growth wins. Your session logs show mood improvement over time. Your Performance Scorecard averages rising month by month IS the celebration.',
  },
];

const ApproachAfterPage = () => {
  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Header />
      <main className="pt-24 pb-20">
        <section className="bg-[#1A1A1A] py-16 md:py-24">
          <div className="container mx-auto px-6 md:px-12 max-w-4xl">
            <Link to="/the-approach" className="inline-flex items-center gap-2 text-white/50 hover:text-white text-sm mb-8 transition-colors" data-testid="back-link">
              <ArrowLeft className="w-4 h-4" /> Back to The Approach
            </Link>
            <p className="text-white/40 text-sm uppercase tracking-[0.2em] mb-2">Area Four</p>
            <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-medium text-white mb-4">After the Round</h1>
            <p className="text-white/70 text-base md:text-lg leading-relaxed max-w-2xl">
              Reflection and growth. How you process a round after it's over determines what you carry into the next one — and whether today's experience becomes tomorrow's improvement.
            </p>
          </div>
        </section>

        <div className="container mx-auto px-6 md:px-12 max-w-4xl">
          <section className="py-12 md:py-16" data-testid="what-it-means">
            <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#1A1A1A] mb-6">Why Reflection Matters</h2>
            <div className="bg-white border border-[#E7E5E4] p-6 md:p-8">
              <p className="text-[#57534E] leading-relaxed mb-4">
                Most golfers leave the course and either celebrate or suffer — then move on without extracting the lessons. The "After" phase is where raw experience becomes refined understanding. It's the difference between 20 years of experience and one year of experience repeated 20 times.
              </p>
              <p className="text-[#57534E] leading-relaxed">
                Research emphasises that <strong>reflection with consequences</strong> accelerates growth. This doesn't mean punishing yourself — it means making the review process meaningful enough that insights stick. Writing it down, tracking it over time, and identifying patterns turns reflection from a vague feeling into a concrete growth strategy.
              </p>
            </div>
          </section>

          <section className="pb-12 md:pb-16" data-testid="testimonial">
            <div className="bg-[#F2F0E9] border-l-4 border-[#1A1A1A] p-6 md:p-8">
              <Quote className="w-8 h-8 text-[#1A1A1A]/20 mb-4" />
              <blockquote className="font-['Playfair_Display'] text-lg md:text-xl text-[#1A1A1A] leading-relaxed mb-4">
                "Alex sees himself as a winner. He finds ways to win as opposed to finding ways to lose. What the thinker thinks, the prover proves."
              </blockquote>
              <p className="text-[#737373] text-sm">— <strong>Karl Morris</strong>, on Alex Noren's mental game transformation</p>
            </div>
          </section>

          <section className="pb-12 md:pb-16" data-testid="three-questions">
            <div className="bg-[#1A1A1A] p-6 md:p-10 text-white">
              <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl mb-6 text-white">The Three Post-Round Questions</h2>
              <p className="text-white/70 mb-6">Answer these within 2 hours of finishing every round. Be specific, not general.</p>
              <div className="grid md:grid-cols-3 gap-6">
                {[
                  { num: '1', question: 'What did I do well?', detail: 'Find at least one genuine positive — a committed swing, a great recovery, steady body language. Start with what worked.' },
                  { num: '2', question: 'Where did I lose shots mentally?', detail: 'Not swing flaws — mental mistakes. Lost focus, poor decisions, emotional reactions that cost you. Be honest but not harsh.' },
                  { num: '3', question: 'What\'s my one commitment for next time?', detail: 'Pick ONE specific thing to work on. Not five. One clear, actionable commitment you can measure.' },
                ].map((q, i) => (
                  <div key={i} className="bg-white/10 p-5">
                    <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center mb-3">
                      <span className="text-lg font-bold">{q.num}</span>
                    </div>
                    <p className="font-semibold text-white mb-2">{q.question}</p>
                    <p className="text-white/60 text-sm">{q.detail}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="pb-12 md:pb-16" data-testid="skills-section">
            <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#1A1A1A] mb-2">Skills to Train</h2>
            <p className="text-[#57534E] mb-8">Turn every round into a learning experience that compounds over time.</p>
            <div className="space-y-8">
              {SKILLS.map((skill, i) => (
                <div key={i} className="bg-white border border-[#E7E5E4] overflow-hidden" data-testid={`skill-${i}`}>
                  <div className="p-6 md:p-8">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-12 h-12 bg-[#1A1A1A] text-white rounded-full flex items-center justify-center flex-shrink-0">
                        <skill.icon className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-2">{skill.title}</h3>
                        <p className="text-[#57534E] leading-relaxed">{skill.description}</p>
                      </div>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4 mt-6">
                      <div className="bg-[#2C5530]/5 p-4">
                        <p className="text-xs text-[#2C5530] uppercase tracking-wider font-semibold mb-2 flex items-center gap-1"><Target className="w-3.5 h-3.5" /> On the Course</p>
                        <p className="text-sm text-[#57534E]">{skill.onCourse}</p>
                      </div>
                      <div className="bg-[#BC562B]/5 p-4">
                        <p className="text-xs text-[#BC562B] uppercase tracking-wider font-semibold mb-2 flex items-center gap-1"><TreePine className="w-3.5 h-3.5" /> Off the Course</p>
                        <p className="text-sm text-[#57534E]">{skill.offCourse}</p>
                      </div>
                      <div className="bg-[#1A1A1A]/5 p-4">
                        <p className="text-xs text-[#1A1A1A] uppercase tracking-wider font-semibold mb-2 flex items-center gap-1"><Activity className="w-3.5 h-3.5" /> Track in QMG</p>
                        <p className="text-sm text-[#57534E]">{skill.inApp}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Two Wolves */}
          <section className="pb-12 md:pb-16" data-testid="two-wolves-section">
            <div className="bg-[#1A1A1A] p-6 md:p-10 text-white">
              <Quote className="w-8 h-8 text-white/20 mb-4" />
              <blockquote className="font-['Playfair_Display'] text-lg md:text-xl text-white leading-relaxed mb-4">
                "A grandfather tells his grandson: 'Inside me, two wolves are fighting. One is anger, fear, self-doubt, and regret. The other is joy, confidence, patience, and growth.' The grandson asks, 'Which one wins?' The grandfather replies: <strong>'The one you feed.'</strong>"
              </blockquote>
              <p className="text-white/40 text-sm mb-6">— Cherokee Parable</p>
              <p className="text-white/70 text-sm leading-relaxed">
                Every post-round reflection is a choice about which wolf you feed. Dwelling on mistakes, replaying failures, and beating yourself up feeds the first wolf. Honest reflection, celebrating growth, extracting lessons, and recommitting to your process feeds the second. The wolf you feed after each round is the one that shows up on the first tee next time.
              </p>
            </div>
          </section>

          <section className="pb-8" data-testid="nav-section">
            <div className="grid md:grid-cols-2 gap-4">
              <Link to="/the-approach/in-between" className="bg-white border border-[#E7E5E4] p-6 hover:border-[#7C3AED]/40 hover:shadow-md transition-all group" data-testid="nav-in-between">
                <p className="text-xs text-[#737373] uppercase tracking-wider mb-1">Previous Area</p>
                <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-2 group-hover:text-[#7C3AED] transition-colors">In-Between Shots</h3>
                <p className="text-sm text-[#57534E]">Recovery, reset, and the mindful walk.</p>
                <ArrowLeft className="w-5 h-5 text-[#7C3AED] mt-3" />
              </Link>
              <Link to="/the-approach/before" className="bg-white border border-[#E7E5E4] p-6 hover:border-[#2C5530]/40 hover:shadow-md transition-all group" data-testid="nav-before">
                <p className="text-xs text-[#737373] uppercase tracking-wider mb-1">Full Circle</p>
                <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-2 group-hover:text-[#2C5530] transition-colors">Before the Round</h3>
                <p className="text-sm text-[#57534E]">Your next round's preparation starts with today's reflection.</p>
                <ArrowRight className="w-5 h-5 text-[#2C5530] mt-3" />
              </Link>
            </div>
            <div className="mt-4 text-center">
              <Link to="/the-approach" className="inline-flex items-center gap-2 text-sm text-[#2C5530] font-medium hover:underline" data-testid="nav-overview">
                <ArrowLeft className="w-4 h-4" /> Back to The Approach Overview
              </Link>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ApproachAfterPage;
